import { TRestaurantFullInfoVisibleForm } from 'modules/CustomersAndRestaurants/Restaurants/RestaurantFullInfo/restaurantFullInfoState/restaurantFullInfoContext';
import React, { useContext, useEffect, useReducer, useRef, useState } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { restaurantsReducer } from './restaurantsReducer';
import {
	IFilterValues,
	RestaurantsContext,
	restaurantsInitialState,
	TRestaurantsCountOfItems,
	TRestaurantsSortBy,
	TTypeOfModalWindow,
} from './restaurantsContext';
import { RestaurantsActionTypes } from './restaurantsActions';
import { ServerErrorTypes, IRestaurants, IGetPaginatedRestaurants, TTypeOfServiceProvided, EntityStatus } from 'types';
import { getPaginatedRestaurants, deleteRestaurant, archiveRestaurant, restoreRestaurant } from 'backend/restaurants';
import axios, { AxiosResponse } from 'axios';
import { useFetchData } from 'hooks/useFetchData';

const RestaurantsState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(restaurantsReducer, restaurantsInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const params = new URL(document.location.href).searchParams;
	const restaurantFullInfoID = params.get('restaurantFullInfoID');
	const { refetch, response, loading } = useFetchData<AxiosResponse<IRestaurants>, IGetPaginatedRestaurants>(
		isMounted.current ? getPaginatedRestaurants : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			countryId: state.IDSelectedCountry,
			cityId: state.IDSelectedCity,
			customerId: state.IDSelectedCustomer,
			clientTypes: state.IDsSelectedClientType,
			status: state.status,
			sortByName: state.sortBy === 'NameA-Z' || state.sortBy === 'NameZ-A' ? state.sortBy : null,
			sortByCustomer: state.sortBy === 'CustomerA-Z' || state.sortBy === 'CustomerZ-A' ? state.sortBy : null,
			sortByCity: state.sortBy === 'CityA-Z' || state.sortBy === 'CityZ-A' ? state.sortBy : null,
			sortByCountry: state.sortBy === 'CountryA-Z' || state.sortBy === 'CountryZ-A' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: RestaurantsActionTypes.SET_RESTAURANTS,
				payload: {
					restaurants: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);
		}
	}, [response]);

	const deleteSelectedItem = async () => {
		try {
			showLoader();
			await deleteRestaurant(state.IDForDeletion);
			const filteredRestaurants = state.restaurants.restaurants.filter((el) => el.id !== state.IDForDeletion);
			dispatch({
				type: RestaurantsActionTypes.SET_RESTAURANTS,
				payload: {
					restaurants: {
						restaurantCount: state.restaurants.restaurantCount - 1,
						restaurants: filteredRestaurants,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			if (axios.isAxiosError(err) && err.response?.data.Type === ServerErrorTypes.RESTAURANT_DELETION_FAILED) {
				dispatch({
					type: RestaurantsActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
					payload: {
						typeOfModalWindow: 'errorActivatedDevices',
					},
				});
			} else {
				dispatch({
					type: RestaurantsActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
					payload: {
						typeOfModalWindow: 'error',
					},
				});
			}
		} finally {
			hideLoader();
		}
	};

	const archiveSelectedItem = async () => {
		try {
			showLoader();
			await archiveRestaurant(state.IDForArchiving);
			const filteredRestaurants = state.restaurants.restaurants.filter((el) => el.id !== state.IDForArchiving);
			dispatch({
				type: RestaurantsActionTypes.SET_RESTAURANTS,
				payload: {
					restaurants: {
						restaurantCount: state.restaurants.restaurantCount - 1,
						restaurants: filteredRestaurants,
					},
					typeOfModalWindow: 'archiveSuccess',
				},
			});
		} catch (err) {
			dispatch({
				type: RestaurantsActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'archiveError',
				},
			});
		} finally {
			hideLoader();
		}
	};

	const restoreSelectedItem = async () => {
		try {
			showLoader();
			await restoreRestaurant(state.IDForRestoring);
			const filteredRestaurants = state.restaurants.restaurants.filter((el) => el.id !== state.IDForRestoring);
			dispatch({
				type: RestaurantsActionTypes.SET_RESTAURANTS,
				payload: {
					restaurants: {
						restaurantCount: state.restaurants.restaurantCount - 1,
						restaurants: filteredRestaurants,
					},
					typeOfModalWindow: 'restoreSuccess',
				},
			});
		} catch (err) {
			dispatch({
				type: RestaurantsActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'restoreError',
				},
			});
		} finally {
			hideLoader();
		}
	};

	const changeSearchString = (value: string) => {
		if (allowChangingSearchString) {
			dispatch({
				type: RestaurantsActionTypes.CHANGE_SEARCH_STRING,
				payload: {
					searchString: value,
				},
			});
		}
	};

	const setIDCountry = (value: Nullable<string>) => {
		dispatch({
			type: RestaurantsActionTypes.SET_ID_SELECTED_COUNTRY,
			payload: {
				IDSelectedCountry: value,
			},
		});
	};

	const setIDCity = (value: Nullable<string>) => {
		dispatch({
			type: RestaurantsActionTypes.SET_ID_SELECTED_CITY,
			payload: {
				IDSelectedCity: value,
			},
		});
	};

	const setIDCustomer = (value: Nullable<string>) => {
		dispatch({
			type: RestaurantsActionTypes.SET_ID_SELECTED_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	};

	const setIDsClientType = (value: TTypeOfServiceProvided[]) => {
		dispatch({
			type: RestaurantsActionTypes.SET_IDs_CLIENT_TYPE,
			payload: {
				IDsSelectedClientType: value,
			},
		});
	};

	const setStatus = (value: EntityStatus) => {
		dispatch({
			type: RestaurantsActionTypes.SET_STATUS,
			payload: {
				status: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: RestaurantsActionTypes.RESET_SELECTED_CONTROLS });

	const setSortBy = (sortBy: TRestaurantsSortBy) => {
		dispatch({
			type: RestaurantsActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TRestaurantsCountOfItems) => {
		dispatch({
			type: RestaurantsActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: RestaurantsActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDForDeletion = (value: string) => {
		dispatch({
			type: RestaurantsActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const setIDForArchiving = (value: string) => {
		dispatch({
			type: RestaurantsActionTypes.SET_ID_FOR_ARCHIVING,
			payload: {
				IDForArchiving: value,
				typeOfModalWindow: 'toConfirmArchiveItem',
			},
		});
	};

	const setIDForRestoring = (value: string) => {
		dispatch({
			type: RestaurantsActionTypes.SET_ID_FOR_RESTORING,
			payload: {
				IDForRestoring: value,
				typeOfModalWindow: 'toConfirmRestoreItem',
			},
		});
	};

	const setFullInfoData = (value: string, activeTab?: TRestaurantFullInfoVisibleForm) => {
		dispatch({
			type: RestaurantsActionTypes.SET_FULL_INFO_DATA,
			payload: {
				fullInfoData: {
					id: value,
					activeTab,
				},
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: RestaurantsActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setFiltersFromQueryString = (value: IFilterValues) => {
		dispatch({
			type: RestaurantsActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: value,
			},
		});
		isMounted.current = true;
		refetch();
	};

	// Open restaurant full info modal after click from customer full info modal
	useEffect(() => {
		if (restaurantFullInfoID) {
			setFullInfoData(restaurantFullInfoID);
		}
	}, [restaurantFullInfoID]);

	return (
		<RestaurantsContext.Provider
			value={{
				deleteSelectedItem,
				archiveSelectedItem,
				restoreSelectedItem,
				changeSearchString,
				setIDCountry,
				setIDCity,
				setIDCustomer,
				setIDsClientType,
				setStatus,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				setIDForArchiving,
				setIDForRestoring,
				setFullInfoData,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				restaurants: state.restaurants,
				searchString: state.searchString,
				IDSelectedCountry: state.IDSelectedCountry,
				IDSelectedCity: state.IDSelectedCity,
				IDSelectedCustomer: state.IDSelectedCustomer,
				IDsSelectedClientType: state.IDsSelectedClientType,
				status: state.status,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				IDForArchiving: state.IDForArchiving,
				IDForRestoring: state.IDForRestoring,
				fullInfoData: state.fullInfoData,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</RestaurantsContext.Provider>
	);
};

export default RestaurantsState;
